/**
 * fontLoader.js — 字体智能加载与持久缓存管理
 *
 * 核心机制：
 *  1. localStorage 持久化：记录已缓存的字体列表，页面刷新后立即恢复状态，无需重新检测
 *  2. SW Cache Storage 验证：启动时对比 localStorage 与实际 SW 缓存，
 *     一旦发现不一致（用户清了浏览器数据），自动重新排队下载
 *  3. 顺序下载队列：严格逐个下载，每个间隔 200ms，不并发，不堵塞网络
 *  4. 排队状态追踪：未下载字体立即标记为"排队中"，UI 可实时显示等待动画
 *  5. 空闲预加载：利用 requestIdleCallback，悄悄在后台完成全部字体缓存
 *
 * 状态流：
 *   页面加载
 *     → 从 localStorage 恢复已知缓存列表（快速路径，立即渲染字形预览）
 *     → 异步验证 SW Cache Storage（检测缓存是否被清除）
 *     → 页面空闲时逐个下载缺失字体
 *     → 每次下载完成更新 localStorage
 */

import { getToken } from './api';
import { API_BASE_URL } from './constants';

// ─── 内部状态 ───────────────────────────────────────────────────────────────
const SW_CACHE_NAME = 'cineart-fonts-v1';
const LS_KEY = 'cineart-fonts-cached'; // localStorage 持久化 key

let _fontListPromise = null;
let _injected = false;
const _loadedFonts = new Set();  // 已确认加载完成的字体 family
const _loadingFonts = new Map(); // 正在下载中的 Promise（family → Promise<boolean>）
const _failedFonts = new Set();  // 已确认加载失败的字体 family
const _queuedFonts = new Set();  // 排队等待下载（由 prefetchAllFontsInIdle 填充，
                                 //   AnnotationModal 通过 isFontLoading/isFontLoaded 判断状态）
let _prefetchStarted = false;    // 防止重复启动空闲预加载
const PREFETCH_RETRY_DELAY_MS = 15000;
const PREFETCH_MAX_RETRIES = 4;

// ─── 工具函数 ────────────────────────────────────────────────────────────────

/** 通知 React 组件字体状态变化，触发重渲染 */
function notifyStateChanged() {
  window.dispatchEvent(new CustomEvent('fontsStateChanged'));
}

/** 获取字体文件的完整 URL */
function fontUrl(font) {
  return `${API_BASE_URL}/fonts/${font.category}/${encodeURIComponent(font.filename)}`;
}

/**
 * 从 localStorage 恢复已知缓存字体列表到内存
 * 这是"快速路径"：不需要等 SW，页面打开就能立即渲染字形预览
 * 注意：SW 缓存被清时此数据可能过时，后续 verifySWCache 会修正
 */
function restoreFromLocalStorage() {
  try {
    const saved = localStorage.getItem(LS_KEY);
    if (!saved) return;
    const families = JSON.parse(saved);
    if (Array.isArray(families)) {
      families.forEach(f => _loadedFonts.add(f));
    }
  } catch {}
}

/** 把当前已加载字体集合持久化到 localStorage */
function saveToLocalStorage() {
  try {
    localStorage.setItem(LS_KEY, JSON.stringify([..._loadedFonts]));
  } catch {}
}

/**
 * 核对 SW Cache Storage 中实际缓存了哪些字体。
 * 作用：检测 SW 缓存被清除的情况，修正 _loadedFonts 状态。
 * 返回：实际在 SW 缓存中的字体 family Set
 */
async function verifySWCache(fonts) {
  if (!('caches' in window)) return new Set();
  try {
    const cache = await caches.open(SW_CACHE_NAME);
    const keys = await cache.keys();
    const cachedUrls = new Set(keys.map(r => r.url));

    const swCachedFamilies = new Set();
    for (const font of fonts) {
      if (cachedUrls.has(fontUrl(font))) {
        swCachedFamilies.add(font.family);
        _loadedFonts.add(font.family); // 同步到内存状态
      }
    }

    // 检测：localStorage 里记录的字体，是否在 SW 缓存里消失了
    const lsSaved = (() => {
      try { return JSON.parse(localStorage.getItem(LS_KEY) || '[]'); } catch { return []; }
    })();
    const missing = lsSaved.filter(f => !swCachedFamilies.has(f));
    if (missing.length > 0) {
      // SW 缓存被清除了，移除内存中不再有效的记录
      missing.forEach(f => _loadedFonts.delete(f));
    }

    saveToLocalStorage();
    notifyStateChanged();
    return swCachedFamilies;
  } catch {
    return new Set();
  }
}

// ─── 公开 API ────────────────────────────────────────────────────────────────

/** 获取字体列表（带缓存，只请求一次；失败或空结果时允许重试） */
function fetchFontList() {
  if (_fontListPromise) return _fontListPromise;
  _fontListPromise = (async () => {
    try {
      const token = getToken();
      const res = await fetch(`${API_BASE_URL}/api/fonts`, {
        headers: token ? { 'Authorization': `Bearer ${token}` } : {}
      });
      if (!res.ok) {
        _fontListPromise = null; // 请求失败，下次允许重试
        return [];
      }
      const fonts = await res.json();
      const list = Array.isArray(fonts) ? fonts : [];
      if (list.length === 0) {
        _fontListPromise = null; // 空结果，下次允许重试（可能是登录前请求）
      }
      return list;
    } catch {
      _fontListPromise = null; // 网络异常，下次允许重试
      return [];
    }
  })();
  return _fontListPromise;
}

/** 注入 @font-face CSS（幂等，只注入一次，不触发字体下载） */
function injectFontFaceCSS(fonts) {
  if (_injected || fonts.length === 0) return;
  const styleId = 'custom-fonts-style';
  let style = document.getElementById(styleId);
  if (!style) {
    style = document.createElement('style');
    style.id = styleId;
    document.head.appendChild(style);
  }
  style.textContent = fonts.map(f => {
    const url = fontUrl(f);
    const fmt = f.format === 'truetype' ? 'truetype'
      : f.format === 'opentype' ? 'opentype'
      : f.format === 'woff' ? 'woff' : 'woff2';
    return `@font-face { font-family: "${f.family}"; src: url("${url}") format("${fmt}"); font-weight: normal; font-style: normal; font-display: swap; }`;
  }).join('\n');
  _injected = true;
}

/**
 * 检查某个字体是否正在下载（同步）
 */
export function isFontLoading(family) {
  if (!family) return false;
  return _loadingFonts.has(family);
}

export function isFontFailed(family) {
  if (!family) return false;
  return _failedFonts.has(family);
}

/**
 * 检查某个字体是否已在内存中确认加载（同步）
 * localStorage 恢复的字体也计入
 */
export function isFontLoaded(family) {
  if (!family || family === 'Arial') return true;
  return _loadedFonts.has(family);
}

/**
 * 检查某个字体是否在排队等待下载（同步）
 */
export function isFontQueued(family) {
  if (!family) return false;
  return _queuedFonts.has(family);
}

/**
 * 下载并加载单个字体（带去重、状态通知、localStorage 持久化）
 */
export async function loadSingleFont(family) {
  if (!family || family === 'Arial') return true;
  if (_loadedFonts.has(family)) return true;
  if (_loadingFonts.has(family)) return _loadingFonts.get(family);
  _failedFonts.delete(family);

  const fonts = await fetchFontList();
  if (fonts.length === 0) return false;
  injectFontFaceCSS(fonts);

  const registered = fonts.find(f => f.family === family);
  if (!registered) {
    _loadedFonts.add(family);
    return true;
  }

  const loadPromise = (async () => {
    try {
      if (document.fonts) {
        const loadedFaces = await document.fonts.load(`16px "${family}"`);
        const isReady = Array.isArray(loadedFaces)
          ? loadedFaces.length > 0 || document.fonts.check(`16px "${family}"`)
          : document.fonts.check(`16px "${family}"`);
        if (!isReady) {
          throw new Error('字体未能激活');
        }
      }
      _loadedFonts.add(family);
      _failedFonts.delete(family);
      saveToLocalStorage(); // 持久化，下次打开页面立即恢复
      return true;
    } catch {
      _failedFonts.add(family);
      return false;
    } finally {
      _loadingFonts.delete(family);
      notifyStateChanged();
    }
  })();

  _loadingFonts.set(family, loadPromise);
  _queuedFonts.delete(family); // 开始实际下载 → 从排队队列移出，进入下载中
  notifyStateChanged(); // 立即通知 UI：该字体从"排队中"变为"加载中"
  return loadPromise;
}

/**
 * 确保自定义字体 CSS 已注入，并恢复 localStorage 缓存状态（快速渲染字形预览）。
 * 可选：传入 requiredFamilies 立即串行加载指定字体。
 */
export async function ensureCustomFontsLoaded(requiredFamilies) {
  // 第一步：从 localStorage 恢复状态（同步，立即生效，渲染已缓存字体的字形）
  restoreFromLocalStorage();
  notifyStateChanged();

  const fonts = await fetchFontList();
  if (fonts.length === 0) return;

  injectFontFaceCSS(fonts);

  if (requiredFamilies && requiredFamilies.length > 0) {
    // 串行加载指定字体（不并发）
    for (const family of requiredFamilies) {
      await loadSingleFont(family).catch(() => {});
    }
  }
}

/**
 * 空闲时后台顺序预加载所有字体（配合 SW 实现永久本地缓存）。
 *
 * 策略：
 *  1. 先与 SW Cache Storage 核对，确认哪些字体真的缓存了，发现丢失自动补充
 *  2. 缺失的字体全部标记为"排队中"（_queuedFonts），UI 立即显示旋转动画
 *  3. 逐个串行下载，间隔 200ms，下载开始时从排队移入下载中（_loadingFonts）
 *  4. 每次下载完更新 localStorage
 *  5. 同一次页面生命周期内只启动一次，页面加载后 3 秒才开始
 */
export function prefetchAllFontsInIdle() {
  if (_prefetchStarted) return;
  _prefetchStarted = true;

  const doFetch = async (attempt = 0) => {
    const fonts = await fetchFontList();
    if (fonts.length === 0) {
      if (attempt < PREFETCH_MAX_RETRIES) {
        setTimeout(() => {
          doFetch(attempt + 1).catch(() => {});
        }, PREFETCH_RETRY_DELAY_MS);
      } else {
        console.warn('[fontLoader] 字体静默预加载重试结束：字体列表仍不可用');
      }
      return;
    }
    injectFontFaceCSS(fonts);

    // 与 SW Cache Storage 核对，修正内存状态，检测缓存丢失
    await verifySWCache(fonts);

    // 真实性验证：localStorage 说"已加载"的字体，用 document.fonts.check() 确认浏览器是否真的激活了
    // 场景：老用户 SW 缓存有文件，localStorage 有记录，但浏览器 FontFace 尚未激活
    //       → 移出 _loadedFonts → 进入 missing → 走排队/下载/动画流程
    if (document.fonts) {
      const notActivated = [];
      for (const family of _loadedFonts) {
        // 系统字体和 Arial 不需要检查
        if (family === 'Arial') continue;
        // 只检查服务器注册的自定义字体
        if (!fonts.find(f => f.family === family)) continue;
        if (!document.fonts.check(`16px "${family}"`)) {
          notActivated.push(family);
        }
      }
      if (notActivated.length > 0) {
        notActivated.forEach(f => _loadedFonts.delete(f));
        saveToLocalStorage();
        notifyStateChanged();
      }
    }

    // 找出所有尚未缓存的字体，逐个串行下载
    const missing = fonts.filter(f =>
      !_loadedFonts.has(f.family) && !_loadingFonts.has(f.family)
    );

    // 将所有缺失字体标记为"排队中"，让 UI 立即显示等待动画
    missing.forEach(f => _queuedFonts.add(f.family));
    if (missing.length > 0) {
      notifyStateChanged(); // 立即通知 UI 更新排队状态
    }

    for (const font of missing) {
      // 每个字体下载前等待浏览器空闲
      await new Promise(r => {
        if ('requestIdleCallback' in window) {
          requestIdleCallback(r, { timeout: 5000 });
        } else {
          setTimeout(r, 100);
        }
      });

      if (_loadedFonts.has(font.family)) {
        _queuedFonts.delete(font.family);
        continue; // 已被用户操作触发加载
      }

      await loadSingleFont(font.family).catch(() => {
        _queuedFonts.delete(font.family);
      });
      await new Promise(r => setTimeout(r, 200)); // 字体间隔 200ms
    }

    // 清空残留的排队状态
    _queuedFonts.clear();

    if (missing.length > 0) {
      // 后台预加载完成
    }
  };

  // 延迟 3 秒后启动，优先保证页面核心内容加载完毕
  setTimeout(() => {
    if ('requestIdleCallback' in window) {
      requestIdleCallback(() => doFetch().catch(() => {}), { timeout: 20000 });
    } else {
      doFetch().catch(() => {});
    }
  }, 3000);
}

/**
 * 仅刷新字体注册表，不清空已加载字体状态。
 * 用于管理员新增字体后，让当前页面重新拉取字体列表并注入新的 @font-face。
 */
export function refreshFontRegistry() {
  _fontListPromise = null;
  _injected = false;
  _prefetchStarted = false;
  notifyStateChanged();
}

/**
 * 强制刷新字体缓存（管理员上传新字体后调用）
 * 同时清除 SW Cache Storage，确保用户不会拿到旧版本字体文件
 */
export function invalidateFontCache() {
  _fontListPromise = null;
  _injected = false;
  _loadedFonts.clear();
  _loadingFonts.clear();
  _failedFonts.clear();
  _queuedFonts.clear();
  _prefetchStarted = false;
  try { localStorage.removeItem(LS_KEY); } catch {}
  // 清除 SW Cache Storage（避免旧字体文件被持续缓存）
  if ('caches' in window) {
    caches.delete(SW_CACHE_NAME).catch(() => {});
  }
  notifyStateChanged();
}
